import { useState } from "react";
import { CLIENTS, clientById } from "../data/clients";
import { useSocialCreds } from "../store/socialCreds";
import { useSettings } from "../store/settings";
import { useTasks } from "../store/tasks";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { SOCIAL_LABEL, SOCIAL_ICON, SOCIAL_COLOR, DEFAULT_BEST_TIMES, type SocialPlatform } from "../data/socialPlatforms";
import { generateSocialIdeasAI, analyzeViralAI, suggestBestTimesAI } from "../lib/claude";

type Tab = "overview" | "ideas" | "viral" | "schedule" | "credentials";

export function SocialMediaView() {
  const [tab, setTab] = useState<Tab>("overview");
  const [clientId, setClientId] = useState<string>(CLIENTS[0]?.id ?? "");

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-xs text-gold">SOCIAL-01</div>
            <h2 className="font-display font-black uppercase text-4xl leading-none">SOCIAL MEDIA</h2>
            <div className="font-serif italic mt-1 text-bone/70">
              hooks · ideias · análise viral · horários · agenda · credenciais
            </div>
          </div>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}
            className="bg-bone text-ink px-3 py-2 font-mono text-[13px] border-2 border-bone">
            {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
          </select>
        </div>
        <div className="flex border-2 border-bone mt-3 flex-wrap">
          {(["overview","ideas","viral","schedule","credentials"] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`tag px-3 py-2 border-r last:border-r-0 border-bone
                ${tab === t ? "bg-bone text-ink" : "text-bone hover:bg-bone/10"}`}>
              {t === "overview"   ? "panorama" :
               t === "ideas"      ? "ideias / hooks" :
               t === "viral"      ? "análise viral" :
               t === "schedule"   ? "horários" :
                                    "credenciais"}
            </button>
          ))}
        </div>
      </header>

      {tab === "overview"    && <Overview clientId={clientId} />}
      {tab === "ideas"       && <IdeasPanel clientId={clientId} />}
      {tab === "viral"       && <ViralPanel clientId={clientId} />}
      {tab === "schedule"    && <SchedulePanel clientId={clientId} />}
      {tab === "credentials" && <CredentialsPanel clientId={clientId} />}
    </div>
  );
}

/* =================== OVERVIEW =================== */
function Overview({ clientId }: { clientId: string }) {
  const accounts = useSocialCreds(s => s.accounts[clientId] ?? []);
  const overrides = useClientBrand(s => s.overrides);
  const tasks    = useTasks(s => s.tasks);
  const cli = clientById(clientId);
  if (!cli) return null;
  const eff = effectiveBrand(cli, overrides[clientId]);
  const socialTasks = tasks.filter(t => t.clientId === clientId && t.tags.some(tag => /social|insta|reel|tiktok|story/i.test(tag)));

  return (
    <div className="space-y-4">
      <section className="border-2 border-ink p-5 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-2">{cli.name} · presença</div>
        <p className="font-crimson italic text-paper text-sm">{cli.pitch}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Stat label="contas vinculadas" value={String(accounts.length)} />
          <Stat label="público-alvo" value={eff.audience.slice(0, 50) + "…"} small />
          <Stat label="demandas sociais ativas" value={String(socialTasks.filter(t => t.status !== "done").length)} />
        </div>
      </section>

      <section className="border-2 border-ink p-5 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-3">contas conectadas</div>
        {accounts.length === 0 ? (
          <p className="font-crimson italic text-paper text-sm">Nenhuma conta. Vai na aba "credenciais" pra adicionar.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {accounts.map(a => (
              <div key={a.platform + a.handle}
                   className="border-2 border-ink p-3 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center font-display font-black"
                     style={{ background: SOCIAL_COLOR[a.platform], color: "#fff" }}>
                  {SOCIAL_ICON[a.platform]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase text-sm">{a.handle}</div>
                  <div className="tag text-graydk">
                    {SOCIAL_LABEL[a.platform]} · id {a.accountId?.slice(0, 12) ?? "—"}…
                  </div>
                  {a.notes && <div className="font-crimson italic text-[11px] text-paper">{a.notes}</div>}
                </div>
                <span className={`tag px-1.5 ${a.accessToken ? "bg-ink text-bone" : "bg-red text-bone"}`}>
                  {a.accessToken ? "token ok" : "sem token"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="border-2 border-ink p-5 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-2">demandas sociais recentes</div>
        {socialTasks.length === 0 ? (
          <p className="font-crimson italic text-paper text-sm">
            Sem demandas com tag social/insta/reel. Crie uma no Quadro de Demandas.
          </p>
        ) : (
          <ul className="space-y-1 font-mono text-[12px]">
            {socialTasks.slice(0, 6).map(t => (
              <li key={t.id} className="flex items-center justify-between border-b border-ink/10 py-1">
                <span className="truncate">{t.title}</span>
                <span className="tag text-graydk shrink-0">{t.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, small }: { label: string; value: string; small?: boolean }) {
  return (
    <div className="border border-ink p-3">
      <div className="tag text-graydk">{label}</div>
      <div className={`font-display font-black ${small ? "text-sm" : "text-2xl"} leading-tight mt-1`}>
        {value}
      </div>
    </div>
  );
}

/* =================== IDEAS =================== */
function IdeasPanel({ clientId }: { clientId: string }) {
  const cli = clientById(clientId);
  const overrides = useClientBrand(s => s.overrides);
  const settings = useSettings();
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [format, setFormat] = useState<"reel"|"carrossel"|"story"|"post"|"tiktok">("reel");
  const [goal, setGoal] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!cli) return;
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setLoading(true);
    try {
      const eff = effectiveBrand(cli, overrides[clientId]);
      const t = await generateSocialIdeasAI({
        apiKey: settings.anthropicKey,
        model: settings.model,
        clientName: cli.name,
        audience: eff.audience,
        voiceTone: eff.voiceTone,
        platform,
        format,
        goal: goal || undefined
      });
      setText(t);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <section className="border-2 border-ink p-4 bg-bone">
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div>
            <div className="tag text-graydk mb-1">plataforma</div>
            <select value={platform} onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              {(["instagram","tiktok","youtube","twitter","linkedin","threads"] as SocialPlatform[]).map(p =>
                <option key={p} value={p}>{SOCIAL_LABEL[p]}</option>
              )}
            </select>
          </div>
          <div>
            <div className="tag text-graydk mb-1">formato</div>
            <select value={format} onChange={(e) => setFormat(e.target.value as any)}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              <option value="reel">Reel / vídeo curto</option>
              <option value="carrossel">Carrossel</option>
              <option value="story">Story</option>
              <option value="post">Post / Foto</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>
          <div>
            <div className="tag text-graydk mb-1">objetivo (opcional)</div>
            <input value={goal} onChange={(e) => setGoal(e.target.value)}
                   placeholder="ex: gerar agendamento de mentoria"
                   className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
          </div>
        </div>
        <div className="flex justify-end">
          <button onClick={generate} disabled={loading}
            className="btn bg-ink text-bone border-ink disabled:opacity-50">
            {loading ? "criando ideias…" : "✦ gerar 10 ideias/hooks"}
          </button>
        </div>
      </section>

      {text && (
        <section className="border-2 border-ink bg-bone">
          <header className="bg-ink text-bone px-4 py-2 flex items-center justify-between">
            <span className="font-display uppercase font-bold">10 ideias / hooks</span>
            <button onClick={() => navigator.clipboard.writeText(text)} className="tag underline">copiar</button>
          </header>
          <pre className="p-5 font-mono text-[13px] whitespace-pre-wrap leading-relaxed">{text}</pre>
        </section>
      )}
    </div>
  );
}

/* =================== VIRAL =================== */
function ViralPanel({ clientId }: { clientId: string }) {
  const cli = clientById(clientId);
  const overrides = useClientBrand(s => s.overrides);
  const settings = useSettings();
  const [ref, setRef] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    if (!ref.trim()) { alert("Cole URL ou descrição do vídeo viral."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setLoading(true);
    try {
      const eff = cli ? effectiveBrand(cli, overrides[clientId]) : undefined;
      const ctx = cli && eff
        ? `${cli.name} (${cli.segment}). Público: ${eff.audience}. ${eff.voiceTone ?? ""}`
        : undefined;
      const t = await analyzeViralAI({
        apiKey: settings.anthropicKey, model: settings.model,
        videoUrlOrDescription: ref, clientContext: ctx
      });
      setText(t);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <section className="border-2 border-ink p-4 bg-bone">
        <div className="tag text-graydk mb-1">URL ou descrição do vídeo viral</div>
        <textarea value={ref} onChange={(e) => setRef(e.target.value)}
                  rows={3}
                  placeholder="cole link do TikTok/Reels OU descreva o vídeo em detalhe (hook, formato, payoff…)"
                  className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px] mb-2" />
        <button onClick={analyze} disabled={loading}
          className="btn bg-ink text-bone border-ink disabled:opacity-50">
          {loading ? "analisando…" : "✦ analisar viral + sugerir adaptação"}
        </button>
      </section>

      {text && (
        <section className="border-2 border-ink bg-bone">
          <header className="bg-ink text-bone px-4 py-2 flex items-center justify-between">
            <span className="font-display uppercase font-bold">análise viral</span>
            <button onClick={() => navigator.clipboard.writeText(text)} className="tag underline">copiar</button>
          </header>
          <pre className="p-5 font-mono text-[13px] whitespace-pre-wrap leading-relaxed">{text}</pre>
        </section>
      )}
    </div>
  );
}

/* =================== SCHEDULE =================== */
function SchedulePanel({ clientId }: { clientId: string }) {
  const cli = clientById(clientId);
  const overrides = useClientBrand(s => s.overrides);
  const accounts = useSocialCreds(s => s.accounts[clientId] ?? []);
  const settings = useSettings();
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!cli) return;
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setLoading(true);
    try {
      const eff = effectiveBrand(cli, overrides[clientId]);
      const t = await suggestBestTimesAI({
        apiKey: settings.anthropicKey, model: settings.model,
        clientName: cli.name, audience: eff.audience, platform
      });
      setAnalysis(t);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <section className="border-2 border-ink p-4 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-2">janelas padrão</div>
        <p className="font-crimson italic text-paper text-sm mb-3">heurísticas iniciais — refine com a análise IA abaixo.</p>
        <div className="grid md:grid-cols-2 gap-2">
          {(["instagram","tiktok","youtube","linkedin"] as SocialPlatform[]).map(p => (
            <div key={p} className="border border-ink p-2">
              <div className="font-display font-bold text-sm">{SOCIAL_LABEL[p]}</div>
              <ul className="font-mono text-[11px] text-paper space-y-0.5 mt-1">
                {DEFAULT_BEST_TIMES[p].map(t => <li key={t}>· {t}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="border-2 border-ink p-4 bg-bone">
        <div className="flex items-end gap-2 mb-2">
          <div className="flex-1">
            <div className="tag text-graydk mb-1">plataforma pra análise refinada</div>
            <select value={platform} onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              {(["instagram","tiktok","youtube","twitter","linkedin","threads"] as SocialPlatform[]).map(p =>
                <option key={p} value={p}>{SOCIAL_LABEL[p]}</option>
              )}
            </select>
          </div>
          <button onClick={run} disabled={loading}
            className="btn bg-ink text-bone border-ink disabled:opacity-50">
            {loading ? "analisando…" : "✦ analisar horários ideais"}
          </button>
        </div>
        {analysis && (
          <pre className="p-3 font-mono text-[13px] whitespace-pre-wrap leading-relaxed border border-ink/30 bg-bone">
{analysis}
          </pre>
        )}
      </section>

      <section className="border-2 border-ink p-4 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-2">publicação / agendamento</div>
        <p className="font-crimson italic text-paper text-sm mb-2">
          publicar direto via Meta Graph API exige proxy backend (CORS bloqueia browser). Use o script Python existente:
          <span className="font-mono not-italic"> instagram/scripts/publish_instagram.py</span>
        </p>
        {accounts.filter(a => a.platform === "instagram").length > 0 && (
          <div className="bg-ink text-bone p-3 font-mono text-[11px] mt-2">
            export IG_ACCOUNT_ID={accounts.find(a => a.platform === "instagram")?.accountId}<br/>
            export IG_TOKEN={accounts.find(a => a.platform === "instagram")?.accessToken?.slice(0, 24)}…
          </div>
        )}
      </section>
    </div>
  );
}

/* =================== CREDENTIALS =================== */
function CredentialsPanel({ clientId }: { clientId: string }) {
  const accounts = useSocialCreds(s => s.accounts[clientId] ?? []);
  const upsert   = useSocialCreds(s => s.upsertAccount);
  const remove   = useSocialCreds(s => s.removeAccount);
  const [showTokens, setShowTokens] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState<{ platform: SocialPlatform; handle: string; accountId: string; accessToken: string; notes: string }>({
    platform: "instagram", handle: "", accountId: "", accessToken: "", notes: ""
  });

  const add = () => {
    if (!draft.handle.trim()) return;
    upsert(clientId, {
      platform: draft.platform,
      handle: draft.handle.trim(),
      accountId: draft.accountId.trim() || undefined,
      accessToken: draft.accessToken.trim() || undefined,
      notes: draft.notes.trim() || undefined
    });
    setDraft({ platform: "instagram", handle: "", accountId: "", accessToken: "", notes: "" });
  };

  return (
    <div className="space-y-3">
      <section className="border-2 border-ink p-4 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-2">contas vinculadas a este cliente</div>
        {accounts.length === 0 ? (
          <p className="font-crimson italic text-paper text-sm">Nenhuma. Adicione abaixo.</p>
        ) : (
          <div className="space-y-2">
            {accounts.map(a => {
              const key = a.platform + a.handle;
              const show = showTokens[key];
              return (
                <div key={key} className="border-2 border-ink p-3 bg-bone">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 flex items-center justify-center font-display font-black"
                         style={{ background: SOCIAL_COLOR[a.platform], color: "#fff" }}>
                      {SOCIAL_ICON[a.platform]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold uppercase">{a.handle}</div>
                      <div className="tag text-graydk">{SOCIAL_LABEL[a.platform]}</div>
                    </div>
                    <button onClick={() => remove(clientId, a.platform, a.handle)}
                            className="tag text-red underline">remover</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono text-[11px]">
                    <div>
                      <div className="tag text-graydk">account id</div>
                      <div className="bg-ink/5 px-2 py-1 break-all">{a.accountId ?? "—"}</div>
                    </div>
                    <div>
                      <div className="tag text-graydk flex items-center gap-2">
                        access token
                        {a.accessToken && (
                          <button onClick={() => setShowTokens(s => ({ ...s, [key]: !s[key] }))}
                                  className="underline">{show ? "ocultar" : "ver"}</button>
                        )}
                      </div>
                      <div className="bg-ink/5 px-2 py-1 break-all">
                        {!a.accessToken ? "—" : show ? a.accessToken : a.accessToken.slice(0, 24) + "…" + a.accessToken.slice(-8)}
                      </div>
                    </div>
                  </div>
                  {a.notes && (
                    <div className="font-crimson italic text-[12px] text-paper mt-2">obs: {a.notes}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="border-2 border-ink p-4 bg-bone">
        <div className="font-display font-bold uppercase text-base mb-3">+ nova conta</div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <div className="tag text-graydk mb-1">plataforma</div>
            <select value={draft.platform}
                    onChange={(e) => setDraft(d => ({ ...d, platform: e.target.value as SocialPlatform }))}
                    className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              {(Object.keys(SOCIAL_LABEL) as SocialPlatform[]).map(p =>
                <option key={p} value={p}>{SOCIAL_LABEL[p]}</option>
              )}
            </select>
          </div>
          <Inp label="handle" value={draft.handle} placeholder="@usuario"
               on={(v) => setDraft(d => ({ ...d, handle: v }))} />
          <Inp label="account id" value={draft.accountId} placeholder="numérico ou business id"
               on={(v) => setDraft(d => ({ ...d, accountId: v }))} />
          <Inp label="access token" value={draft.accessToken} placeholder="long-lived token"
               on={(v) => setDraft(d => ({ ...d, accessToken: v }))} type="password" />
          <div className="md:col-span-2">
            <Inp label="notas" value={draft.notes} placeholder="renovar em XX/XX, conta business…"
                 on={(v) => setDraft(d => ({ ...d, notes: v }))} />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={add} className="btn btn-hire">+ adicionar conta</button>
        </div>
      </section>

      <p className="font-crimson italic text-graydk text-[11px] text-center">
        credenciais ficam no localStorage do navegador · não trafegam por servidor · cuidado em máquinas compartilhadas
      </p>
    </div>
  );
}

function Inp({ label, value, on, placeholder, type = "text" }:
  { label: string; value: string; on: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <div>
      <div className="tag text-graydk mb-1">{label}</div>
      <input type={type} value={value} placeholder={placeholder}
             onChange={(e) => on(e.target.value)}
             className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
    </div>
  );
}
